import { runInAction, when } from "mobx";

import { wait } from "@utils/wait";

import { authService } from "../auth";
import { storageService } from "../storage";

import type { Wallet } from "./types";

const MAX_ROOT_TOKEN_RETRIES = 10;
const RETRY_DELAY_SEC = 2;
const MIN_REQUEST_INTERVAL_MS = 5 * 60 * 1000;

export interface TokenCallbacks {
  setRootAccountToken: (token: string | null) => void;
  getTokensLoaded: () => boolean;
  getRootAccountToken: () => string | null;
  getRootAccountTokenLastRequested: () => number;
  getIsGeneratingRootToken: () => boolean;
  setIsGeneratingRootToken: (value: boolean) => void;
}

export class TokenManager {
  generateRootAccountToken = async (wallet: Wallet, callbacks: TokenCallbacks, force = false): Promise<void> => {
    if (!wallet.account || callbacks.getIsGeneratingRootToken()) return;

    runInAction(() => {
      callbacks.setIsGeneratingRootToken(true);
    });

    try {
      if (!force) {
        if (!callbacks.getTokensLoaded()) {
          await when(() => callbacks.getTokensLoaded(), { timeout: 3000 }).catch(() => {});
          if (callbacks.getRootAccountToken() && !authService.isTokenExpired(callbacks.getRootAccountToken()!)) {
            return;
          }
        }

        if (callbacks.getRootAccountToken() && !authService.isTokenExpired(callbacks.getRootAccountToken()!)) {
          return;
        }

        // Only apply rate limit if we have a valid (non-expired) token
        // If token is expired or missing, we need to generate a new one regardless of last request time
        const currentToken = callbacks.getRootAccountToken();
        const hasValidToken = currentToken && !authService.isTokenExpired(currentToken);
        if (hasValidToken && Date.now() - callbacks.getRootAccountTokenLastRequested() < MIN_REQUEST_INTERVAL_MS) {
          return;
        }
      }

      await this.tryGenerateRootToken(wallet, callbacks, force);
    } finally {
      runInAction(() => {
        callbacks.setIsGeneratingRootToken(false);
      });
    }
  };

  private tryGenerateRootToken = async (
    wallet: Wallet,
    callbacks: Pick<TokenCallbacks, "setRootAccountToken">,
    force: boolean,
  ): Promise<void> => {
    for (let attempt = 0; attempt < MAX_ROOT_TOKEN_RETRIES; attempt++) {
      try {
        if (!force && attempt === 0) {
          const rootToken = await storageService.getRootToken();
          if (rootToken && !authService.isTokenExpired(rootToken)) {
            callbacks.setRootAccountToken(rootToken);
            return;
          }
        }

        if (attempt > 0) await wait(RETRY_DELAY_SEC);

        const token = await authService.generateRootAccountToken(wallet);

        if (token) {
          callbacks.setRootAccountToken(token);
          return;
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        const isUserRejected = /reject|denied|user has rejected/i.test(msg);
        if (isUserRejected) break;
        if (attempt >= MAX_ROOT_TOKEN_RETRIES - 1) break;
        await wait(RETRY_DELAY_SEC);
      }
    }
  };

  loadTokensFromSession = async (
    callbacks: Pick<TokenCallbacks, "setRootAccountToken"> & {
      setTokensLoaded: (value: boolean) => void;
      setRootAccountTokenLastRequested: (value: number) => void;
    },
  ): Promise<void> => {
    try {
      const rootToken = await storageService.getRootToken();
      const lastRequested = await storageService.getRootTokenLastRequested();
      if (rootToken && !authService.isTokenExpired(rootToken)) {
        runInAction(() => {
          callbacks.setRootAccountToken(rootToken);
          if (lastRequested) {
            callbacks.setRootAccountTokenLastRequested(lastRequested);
          }
        });
      }
    } catch {
      // Silent - token loading failure is handled by generating new tokens
    } finally {
      runInAction(() => {
        callbacks.setTokensLoaded(true);
      });
    }
  };
}

export const tokenManager = new TokenManager();
